import { AxiosInstance } from 'axios'
import { Result } from '../../../Shared/Protocol/protocol-result'
import {
  HCPFlowEnum,
  IHCPFlowKanbanPayload,
  IHCPFlowKanbanResponse,
  IHCPFinishedPayload,
  IHCPFlowPositionPayload,
  IHCPFlowStatus,
  IHCPFlowStatusInput,
} from './hcp-flow.interface'
import { buildKanbanResponse, summarizeRawPayload } from './hcp-flow.mapper'

const KANBAN_CACHE_TTL_MS = 5 * 60 * 1000
const MAX_KANBAN_CACHE_ENTRIES = 100
interface CachedPayload {
  value: unknown
  fetchedAt: number
}

export class HCPFlowService {
  private readonly flowStatusesCache = new Map<HCPFlowEnum, CachedPayload>()
  private readonly flowStatusesRequests = new Map<HCPFlowEnum, Promise<Result<unknown>>>()
  private readonly kanbanRawCache = new Map<string, CachedPayload>()
  private readonly kanbanRawRequests = new Map<string, Promise<Result<unknown>>>()
  private cacheRevision = 0
  private readonly uncertainBatchFinishes = new Set<string | number>()

  constructor(private readonly api: AxiosInstance) {
    // Also covers writes made outside this service, such as batch dialogs and art actions.
    this.api.interceptors?.response?.use(
      (response) => {
        this.invalidateAfterWrite(response.config)
        return response
      },
      (error) => {
        this.invalidateAfterWrite(error?.config)
        return Promise.reject(error)
      }
    )
  }

  private invalidateAfterWrite(config?: { method?: string; url?: string }): void {
    if (!config?.method || ['get', 'head', 'options'].includes(config.method.toLowerCase())) return
    const path = (config.url ?? '').replace(/^https?:\/\/[^/]+/i, '').replace(/^\/api(?=\/)/, '')
    if (/^\/(orders?|replacement-orders|order-products|production-batches|hcp-pre-service-orders|hcp-flows|customization-flow|approved-arts|arts?|login|refresh-token)(?:\/|\?|$)/.test(path)) {
      this.clearKanbanRequestCache()
      this.clearFlowStatusesCache()
    }
  }

  private buildParams(payload: IHCPFlowKanbanPayload): Record<string, string | number | boolean> {
    const { page = 1, offset = 50, searchBy, externalOsNumber, allArtsApproved, flowDeadlineStart, flowDeadlineEnd } = payload
    const params: Record<string, string | number | boolean> = { page, offset }

    if (searchBy?.trim()) params.searchBy = searchBy.trim()
    if (externalOsNumber?.trim()) params.externalOsNumber = externalOsNumber.trim()
    if (allArtsApproved !== undefined) params.allArtsApproved = allArtsApproved
    if (flowDeadlineStart) params.flowDeadlineStart = flowDeadlineStart
    if (flowDeadlineEnd) params.flowDeadlineEnd = flowDeadlineEnd

    return params
  }

  private buildKanbanRequestKey(payload: IHCPFlowKanbanPayload): string {
    return JSON.stringify({
      scope: payload.cacheScope ?? '',
      flow: payload.flow,
      ...this.buildParams(payload),
    })
  }

  private invalidateFlowStatuses(flow: HCPFlowEnum): void {
    this.flowStatusesCache.delete(flow)
    this.flowStatusesRequests.delete(flow)
  }

  public async getFlowStatuses(flow: HCPFlowEnum): Promise<Result<unknown>> {
    const cached = this.flowStatusesCache.get(flow)
    if (cached && Date.now() - cached.fetchedAt < KANBAN_CACHE_TTL_MS) {
      return Result.ok(cached.value)
    }
    this.flowStatusesCache.delete(flow)

    const pendingRequest = this.flowStatusesRequests.get(flow)
    if (pendingRequest) return pendingRequest

    const revision = this.cacheRevision
    const request = (async (): Promise<Result<unknown>> => {
      try {
        const response = await this.api.get(`/hcp-flows/${flow}/statuses`)
        if (revision === this.cacheRevision) {
          this.flowStatusesCache.set(flow, { value: response.data, fetchedAt: Date.now() })
        }
        return Result.ok(response.data)
      } catch (error: any) {
        return Result.fail(error?.response?.data ?? error)
      }
    })()

    this.flowStatusesRequests.set(flow, request)
    try {
      return await request
    } finally {
      if (this.flowStatusesRequests.get(flow) === request) this.flowStatusesRequests.delete(flow)
    }
  }

  public async getKanbanRaw(payload: IHCPFlowKanbanPayload): Promise<Result<unknown>> {
    const requestKey = this.buildKanbanRequestKey(payload)
    if (payload.forceRefresh) {
      this.clearKanbanRequestCache()
    }
    const cached = this.kanbanRawCache.get(requestKey)
    if (cached && Date.now() - cached.fetchedAt < KANBAN_CACHE_TTL_MS) {
      return Result.ok(cached.value)
    }
    this.kanbanRawCache.delete(requestKey)
    const pendingRequest = this.kanbanRawRequests.get(requestKey)
    if (pendingRequest) return pendingRequest

    const revision = this.cacheRevision
    const request = (async (): Promise<Result<unknown>> => {
      try {
        const { flow } = payload
        const response = await this.api.get(`/order-products/hcp-flow/${flow}/kanban`, {
          params: this.buildParams(payload),
        })
        if (revision === this.cacheRevision) {
          this.kanbanRawCache.forEach((entry, key) => {
            if (Date.now() - entry.fetchedAt >= KANBAN_CACHE_TTL_MS) this.kanbanRawCache.delete(key)
          })
          if (this.kanbanRawCache.size >= MAX_KANBAN_CACHE_ENTRIES) {
            const oldestKey = this.kanbanRawCache.keys().next().value
            if (oldestKey !== undefined) this.kanbanRawCache.delete(oldestKey)
          }
          this.kanbanRawCache.set(requestKey, { value: response.data, fetchedAt: Date.now() })
        }
        return Result.ok(response.data)
      } catch (error: any) {
        return Result.fail(error?.response?.data ?? error)
      }
    })()

    this.kanbanRawRequests.set(requestKey, request)
    try {
      return await request
    } finally {
      if (this.kanbanRawRequests.get(requestKey) === request) this.kanbanRawRequests.delete(requestKey)
    }
  }

  public clearKanbanRequestCache(): void {
    this.cacheRevision += 1
    this.kanbanRawCache.clear()
    this.kanbanRawRequests.clear()
  }

  public clearFlowStatusesCache(flow?: HCPFlowEnum): void {
    if (flow) {
      this.invalidateFlowStatuses(flow)
      return
    }

    this.flowStatusesCache.clear()
    this.flowStatusesRequests.clear()
  }

  public async getKanban(
    payload: IHCPFlowKanbanPayload
  ): Promise<Result<IHCPFlowKanbanResponse>> {
    const { flow, page = 1, offset = 50 } = payload
    if (payload.forceRefresh) {
      this.clearKanbanRequestCache()
      this.clearFlowStatusesCache()
    }

    const [statusesResult, kanbanResult] = await Promise.all([
      this.getFlowStatuses(flow),
      this.getKanbanRaw({ ...payload, forceRefresh: false }),
    ])

    if (!kanbanResult.isSuccess) {
      return Result.fail(
        (kanbanResult.error ?? statusesResult.error) as Error
      )
    }

    const board = buildKanbanResponse(
      flow,
      page,
      offset,
      statusesResult.isSuccess ? statusesResult.getValue() : [],
      kanbanResult.isSuccess ? kanbanResult.getValue() : { columns: [] }
    )
    board.fetchedAt = this.kanbanRawCache.get(this.buildKanbanRequestKey(payload))?.fetchedAt ?? Date.now()

    if (process.env.NODE_ENV === 'development') {
      console.info('[HCP Flow]', {
        flow,
        statuses: summarizeRawPayload(statusesResult.getValue()),
        kanban: summarizeRawPayload(kanbanResult.getValue()),
        parsedColumns: board.columns.length,
        parsedCards: board.total,
      })
    }

    return Result.ok(board)
  }

  public async createFlowStatus(
    flow: HCPFlowEnum,
    payload: IHCPFlowStatusInput
  ): Promise<Result<IHCPFlowStatus>> {
    try {
      const response = await this.api.post(`/hcp-flows/${flow}/statuses`, payload)
      this.invalidateFlowStatuses(flow)
      return Result.ok(response.data as IHCPFlowStatus)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async updateFlowStatus(
    flow: HCPFlowEnum,
    statusId: string,
    payload: Partial<IHCPFlowStatusInput>
  ): Promise<Result<IHCPFlowStatus>> {
    try {
      const response = await this.api.put(`/hcp-flows/${flow}/statuses/${statusId}`, payload)
      this.invalidateFlowStatuses(flow)
      return Result.ok(response.data as IHCPFlowStatus)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async updateFlowPosition(
    productionBatchId: string | number,
    payload: IHCPFlowPositionPayload
  ): Promise<Result<unknown>> {
    try {
      const response = await this.api.post(
        `/production-batches/${productionBatchId}/hcp-flow/position`,
        payload
      )
      return Result.ok(response.data)
    } catch (error: any) {
      const responseData = error?.response?.data
      const failurePayload = typeof responseData === 'object' && responseData !== null
        ? { ...responseData, status: error?.response?.status }
        : {
          error: String(responseData ?? error?.message ?? 'Erro ao mover card.'),
          status: error?.response?.status,
        }

      return Result.fail(failurePayload)
    }
  }

  public async updateGroupedLogisticsOrderPosition(
    orderId: string | number,
    hcpFlowStatusId: string
  ): Promise<Result<unknown>> {
    try {
      const response = await this.api.post(
        `/orders/${orderId}/hcp-flow/logistics/position`,
        { hcpFlowStatusId }
      )
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async updatePreServiceOrderFlowPosition(
    hcpPreServiceOrderId: string | number,
    payload: IHCPFlowPositionPayload
  ): Promise<Result<unknown>> {
    try {
      const response = await this.api.post(
        `/hcp-pre-service-orders/${hcpPreServiceOrderId}/hcp-flow/position`,
        payload
      )
      return Result.ok(response.data)
    } catch (error: any) {
      const responseData = error?.response?.data
      const failurePayload = typeof responseData === 'object' && responseData !== null
        ? { ...responseData, status: error?.response?.status }
        : {
          error: String(responseData ?? error?.message ?? 'Erro ao mover Pré-os.'),
          status: error?.response?.status,
        }

      return Result.fail(failurePayload)
    }
  }

  public async updateProductionBatchHCPFinished(
    productionBatchId: string | number,
    payload: IHCPFinishedPayload
  ): Promise<Result<unknown>> {
    if (this.uncertainBatchFinishes.has(productionBatchId)) {
      try {
        const current = await this.api.get(`/production-batches/${productionBatchId}`)
        this.uncertainBatchFinishes.delete(productionBatchId)
        if (current.data?.hcpFinished === true) return Result.ok(current.data)
      } catch (error: any) {
        return Result.fail(error?.response?.data ?? error)
      }
    }
    try {
      const response = await this.api.patch(
        `/production-batches/${productionBatchId}/hcp-finished`,
        payload
      )
      return Result.ok(response.data)
    } catch (error: any) {
      if (!error?.response || error?.code === 'ECONNABORTED') {
        this.uncertainBatchFinishes.add(productionBatchId)
        try {
          const current = await this.api.get(`/production-batches/${productionBatchId}`)
          this.uncertainBatchFinishes.delete(productionBatchId)
          if (current.data?.hcpFinished === true) return Result.ok(current.data)
        } catch {
          // Preserve the original failure when the verification is unavailable.
        }
      }
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async bulkMoveSheetItemsToPreServiceOrderGeneration(
    orderProductIds: Array<string | number>
  ): Promise<Result<unknown>> {
    try {
      const response = await this.api.post('/order-products/hcp-flow/bulk-position', {
        productionBatchIds: orderProductIds,
      })
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async generatePreServiceOrder(
    orderProductIds?: Array<string | number>
  ): Promise<Result<unknown>> {
    try {
      const response = await this.api.post('/hcp-pre-service-orders/generate', {
        ...(orderProductIds?.length ? { productionBatchIds: orderProductIds } : {}),
      })
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public static isFlowEnabled(flow: HCPFlowEnum): boolean {
    return (
      flow === HCPFlowEnum.AWAITING_ART
      || flow === HCPFlowEnum.SHEET
      || flow === HCPFlowEnum.PRODUCTION
      || flow === HCPFlowEnum.MACHINE_PRODUCTION
      || flow === HCPFlowEnum.LOGISTICS
    )
  }
}
